import moment from './moment';
import constants from '../constants';
import store from 'store';
import { getViewData } from './view-data';

/**
 * TODO: Revisit this whole file. It got complicated again.
 *
 * This file should be responsible for:
 *  - Calculating the minimum short term data
 *    - Take into account availability
 *    - Take into account overnight booking setting
 *    - Take into account access hours
 *    - Take into account the time limit for same day bookings
 *  - Calculating the minimum long term data
 *  - Calculating the default short term data
 *    - Take into account availability
 *    - Take into account overnight booking setting
 *    - Take into account access hours
 *    - Take into account the time limit for same day bookings
 *  - Calculating the default long term data
 */


/**
 * Calculate and set the default dates for long
 * term and short term bookings
 *
 * @param data
 */
export function calculateDateDefaults(data) {
    if (data.rental === 'short') {
        return calculateDefaultShortTerm(data);
    }

    return calculateDefaultLongTerm(data);
}

/**
 * Ensure the current date is valid. If not, set the
 * start to the minimum allowed
 *
 * @param data
 * @returns {*}
 */
export function ensureDatesAreValid(data) {
    let start = startMoment(data);
    if (data.rental === 'long') {
        if (start.isBefore(minimumLongTermDate())) {
            data.start_date = minimumLongTermDate().format('DD-MM-YYYY');
        }

        return data;
    }

    const payByPhone = localStorage.getItem('search-payByPhone');
    const pastBooking = Boolean(localStorage.getItem('search-bookingType') === 'past');

    if (start.isBefore(minimumShortTermDate()) && !payByPhone && !pastBooking) {
        data.start_date = minimumShortTermDate().format('DD-MM-YYYY');
        data.start_time = minimumShortTermDate().format('HH:mm');
    }

    return data;
}

/**
 * Minimum datetime for short term bookings
 * We round down so that the user can start their
 * booking from the previous half hour
 *
 * @returns {*}
 */
export function minimumShortTermDate() {
    return moment().floor(30, 'minutes');
}
/**
 * Minimum datetime for short term bookings
 *
 * We don't round for book now bookings
 *
 * @returns {*}
 */
export function minimumShortTermDateNow() {
    return moment();
}

/**
 * Minimum datetime for long term bookings
 *
 * @returns {*}
 */
export function minimumLongTermDate() {
    return new moment().add(constants.LONG_TERM_DAY_OFFSET, 'days');
}

/**
 * Work out the default long term date
 */
export function defaultLongTermDate() {
    return new moment().add(constants.DEFAULT_LONG_TERM_DAY_OFFSET, 'days');
}

/**
 * Work out the default short term date
 */
export function defaultShortTerm() {
    // Create new date instance and set default time to 9:00
    let start = new moment().set({
        hours: 9,
        minutes: 0,
    });

    // if it's after time limit the same day, set the date to the following day
    if (moment().isAfter(shortTermBookingLimit())) {
        start.add(1, 'days');
    }

    // Set availability based on provided values
    const unavailableDates = getViewData('availability.unavailable.days', []);

    // Get the the next available day
    while (unavailableDates.includes(start.format('YYYY-MM-DD'))) {
        start.add(1, 'day');
    }

    // Get the initial start date, as it may change in the next iterations
    // of the availableTimes loop - this will prevent an infinite loop
    // in case unavailableTimes & unavailableAccessTimes together cover
    // every possible time within 24-hours, which already happened in the past.
    let startDay = start.format('YYYY-MM-DD');

    // Get the the next available time slot
    let unavailableTimes = getViewData(`availability.unavailable.hours[${startDay}]`, []);
    let unavailableAccessTimes = getViewData('availability.unavailable_access_times', []);

    while (unavailableTimes.includes(start.format('HH:mm')) || unavailableAccessTimes.includes(start.format('HH:mm'))) {
        start.add(30, 'minutes');
        // prevent an infinite loop by unavailable times
        // for the current day in case day changed during this iteration
        if (startDay !== start.format('YYYY-MM-DD')) {
            startDay = start.format('YYYY-MM-DD');
            unavailableTimes = getViewData(`availability.unavailable.hours[${startDay}]`, []);
        }
    }

    return start;
}

/**
 * Set the default short term end time
 */
export function defaultShortTermEnd() {
    return new moment().set({
        hours: 11,
        minutes: 0,
    });
}

/**
 * Calculate the default short term date/time
 * Takes into account the maximum time permitted to book the current day
 * and any provided availability
 *
 * @param data
 */
export function calculateDefaultShortTerm(data) {
    // Get the currently selected datetime data for us to validate
    let originalStart = startMoment(data);

    let minShortTermDate = defaultShortTerm();

    // Set the start date
    if (originalStart.isBefore(minShortTermDate)) {
        data.start_date = minShortTermDate.format('DD-MM-YYYY');
        data.start_time = minShortTermDate.format('HH:mm');

        // We'll base the end date off the start date
        let end = minShortTermDate.clone();

        // Default the length to 8 hours
        end.add(2, 'hour');

        // Set the end date
        data.end_date = end.format('DD-MM-YYYY');
        data.end_time = end.format('HH:mm');
    }

    // Check that the end date again to make sure it's still valid
    let newStart = startMoment(data);
    let newEnd = endMoment(data);

    if (newEnd.isBefore(newStart)) {
        let end = minShortTermDate.clone();
        // Default the length to 8 hours
        end.add(2, 'hour');

        // Set the end date
        data.end_date = end.format('DD-MM-YYYY');
        data.end_time = end.format('HH:mm');
    }

    return data;
}

/**
 * Calculate the default long term date
 *
 * @param data
 */
export function calculateDefaultLongTerm(data) {
    // Set the default start date - always 2 days in advance for long term bookings
    let defaultStart = defaultLongTermDate();

    // Let's check if there's a search date already set within the store.
    // If so we need to use it to persist the users chosen search dates when on the results page.
    let searchDate = moment(store.state.search.currentSearch.start_date, 'DD-MM-YYYY');

    // Just in case we're getting an old search date from local storage we need to double check it isn't before today e.g. invalid
    // If it is invalid, replace it with the defaultStart date set above.
    if (searchDate.isBefore(moment().startOf('day'))) {
        searchDate = defaultStart;
    }

    // We need to set the data start_date because that's what our datepickers will use.
    // If there are no search results (i.e. we're on the site homepage) - set it to the defaultStart date
    // If there are results then we'll set it to the search date.
    const results = store.state.search.results;
    data.start_date = !results.length ? defaultStart.format('DD-MM-YYYY') : searchDate.format('DD-MM-YYYY');

    return data;
}

/**
 * The maximum time before the short term
 * booking default is defaulted to the next day
 *
 * @returns {moment.Moment}
 */
export function shortTermBookingLimit() {
    return moment().set({
        hour:   9,
        minute: 0,
        second: 0,
    });
}

/**
 * Return a moment instance of the start data
 *
 * @param data
 * @returns {*|moment.Moment}
 */
export function startMoment(data) {
    let start = data.start_date;
    let format = 'DD-MM-YYYY';
    if (data.rental === 'short') {
        start += ' ' + data.start_time;
        format += ' HH:mm';
    }

    return moment(start, format);
}

/**
 * Return a moment instance of the end data
 *
 * @param data
 * @returns {*|moment.Moment}
 */
export function endMoment(data) {
    let end = data.end_date;
    let format = 'DD-MM-YYYY';
    if (data.rental === 'short') {
        end += ' ' + data.end_time;
        format += ' HH:mm';
    }

    return moment(end, format);
}

/**
 * Return a moment instance of the next payment date for long term bookings
 *
 * @param data
 * @returns {*|moment.Moment}
 */
export function nextPaymentDate(data) {
    if (data.monthlyDepositRequired) {
        return new moment(data.startDate);
    }

    return new moment(data.startDate, 'DD-MM-YYYY').add(1, 'months').subtract(2, 'days');
}
